import React, {
  FunctionComponent,
  useContext,
  useEffect,
  useState,
} from "react";
import {
  useTranslate,
  useCosts,
  $$,
  makePlanUrlPartForApi,
} from "../../helpers";
import { PharmacyType, Plan, PlanDetailsPageSection } from "../../@types";
import { CheckmarkIcon, PlanDetailsLink } from "../../components";
import { AppContext } from "../../app/store";
import {
  shouldSuppressDrugPricing,
  shouldSuppressPlanBenefits,
} from "../../helpers/planHelpers";
import { getOtherDrugInfo } from "../../api";

const DrugCosts: FunctionComponent<{
  plans: Plan[];
  selectedPlan: Plan | undefined;
}> = ({ plans, selectedPlan }) => {
  const t = useTranslate();
  const { state } = useContext(AppContext);

  const CoveredDrugs: FunctionComponent<{
    plan: Plan;
  }> = ({ plan }) => {
    const [coveredDrugs, setCoveredDrugs] = useState<number | undefined>(
      undefined
    );

    const getCoveredDrugs = async (): Promise<void> => {
      if (state.prescriptions.length) {
        const ndcs = state.prescriptions.map(p => p.ndc);

        const planUrl = makePlanUrlPartForApi(plan);
        try {
          const otherDrugInfo = await getOtherDrugInfo(ndcs, planUrl);

          const covered =
            otherDrugInfo?.reduce(
              (prev, curr) => prev + (curr.on_formulary ? 1 : 0),
              0
            ) ?? 0;

          setCoveredDrugs(covered);
        } catch (_e) {
          setCoveredDrugs(undefined);
        }
      }
    };

    useEffect(() => {
      getCoveredDrugs();
    }, []);

    return (
      <>
        {shouldSuppressPlanBenefits(plan) ? (
          <div>{t("suppressed_plan_benefit")}</div>
        ) : coveredDrugs === undefined ? (
          <div className="ds-u-color--error">{t("error.drug_info")}</div>
        ) : (
          <>
            <div className="DrugCosts__label">{`${coveredDrugs} ${t("of")} ${
              state.prescriptions.length
            }`}</div>
            <div>{t("plan_terms.prescription_drugs_covered")}</div>
            <PlanDetailsLink
              className="mct-hide-print"
              label={t("restrictions_may_apply")}
              plan={plan}
              section={PlanDetailsPageSection.DRUG_COVERAGE}
            />
          </>
        )}
      </>
    );
  };

  const TotalDrugCosts: FunctionComponent<{ plan: Plan }> = ({ plan }) => {
    const { costs } = useCosts(plan);

    const getPharmacyName = (npi: string): string => {
      if (npi === "") {
        return t("plan_search_prefs.mail_order_pharmacy");
      }

      const pharmacy = state.pharmacies.find(p => p.npi === npi);

      return pharmacy ? pharmacy.name : "N/A";
    };
    const suppressDrugPricing = shouldSuppressDrugPricing(plan);
    const showMailOrderNotCovered =
      !costs.find(p => p.mail_order === true) &&
      [PharmacyType.MAIL_AND_RETAIL, PharmacyType.MAIL].includes(
        state.pharmacyType
      );

    const mailOrderNotCoveredElem = (
      <div
        className="ds-u-margin-bottom--2"
        data-testid="mail-order-not-covered"
      >
        <span>{getPharmacyName("")}</span>
        <div>
          <CheckmarkIcon checked={false} />
          {` `}
          {t("plan_terms.does_not_cover_mail_order")}
        </div>
      </div>
    );

    return (
      <>
        {!costs.length && showMailOrderNotCovered && mailOrderNotCoveredElem}

        {costs.length > 0 && (
          <>
            <div>
              {costs.map(pharmacy => {
                if (
                  pharmacy.mail_order &&
                  [PharmacyType.RETAIL, PharmacyType.NONE].includes(
                    state.pharmacyType
                  )
                ) {
                  return null;
                }

                return (
                  <div
                    key={pharmacy.npi}
                    className="ds-u-margin-bottom--2 e2e-pc-pharmacy"
                  >
                    <span>{getPharmacyName(pharmacy.npi)}</span>
                    <div>
                      <CheckmarkIcon checked={pharmacy.in_network} />
                      {` `}
                      {pharmacy.preferred
                        ? t("pharmacy.terms.preferred_network")
                        : pharmacy.in_network
                        ? t("pharmacy.terms.standard_in_network")
                        : t("pharmacy.terms.out_of_network")}
                    </div>
                    <span className="ds-u-font-weight--bold">
                      {suppressDrugPricing ? (
                        <i>{t("plan_terms.drug_pricing_suppressed")}</i>
                      ) : (
                        $$(
                          plan.total_remaining_premium +
                            pharmacy.estimated_yearly_total
                        )
                      )}
                    </span>
                  </div>
                );
              })}
              {showMailOrderNotCovered && mailOrderNotCoveredElem}
            </div>
          </>
        )}
      </>
    );
  };

  return (
    <>
      <tr>
        <td
          className="PlanComparePage__tableHeader"
          colSpan={selectedPlan ? 1 : plans.length + 1}
        >
          {t("plan_terms.drug_coverage_and_costs")}
        </td>
      </tr>

      <tr>
        <td className="PlanComparePage__tableLabelColumn">
          {t("plan_terms.drugs_covered")}
        </td>
        {plans.map((plan, index) => (
          <td
            key={index}
            className={`${
              selectedPlan?.id === plan.id
                ? "PlanComparePage__selectedCell"
                : ""
            }`}
          >
            <span className="ds-text-heading--md ds-u-display--block ds-u-lg-display--none mct-hide-print">
              {t("plan_terms.drugs_covered")}
            </span>
            <CoveredDrugs plan={plan} />
          </td>
        ))}
      </tr>
      <tr>
        <td className="PlanComparePage__tableLabelColumn PlanComparePage__lastRowCell">
          {t("plan_terms.estimated_yearly_total_cost")}
        </td>
        {plans.map((plan, index) => (
          <td
            key={index}
            className={`PlanComparePage__lastRowCell ${
              selectedPlan?.id === plan.id
                ? "PlanComparePage__selectedCell"
                : ""
            }`}
          >
            <span className="ds-text-heading--md ds-u-display--block ds-u-lg-display--none mct-hide-print">
              {t("plan_terms.estimated_yearly_total_cost")}
            </span>
            <TotalDrugCosts plan={plan} />
          </td>
        ))}
      </tr>
    </>
  );
};

export default DrugCosts;
